/**
 * Auto-generated from @intelishs-group/schema-core v5.1.0
 * Source: catalogCoreTypes — Catalog API — solo endpoints no cubiertos por @intelishs-group/catalog-core (HR/admin). Para GenericCatalog usar la lib externa directamente.
 * Generated: 2026-10-09
 * Do not edit manually. Run: npm run generate:services
 */
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ApiBaseService } from './api-base.service';
import type {
  ActualizarBrokerRequest,
  ActualizarClaveSatRequest,
  BooleanResponseDto,
  BrokerResponsePagedResponseResponseDto,
  BrokerResponseResponseDto,
  CambiarEstatusClaveSatRequest,
  CampoEspecialTipoDatoIEnumerableResponseDto,
  ClaseSatVectorResponseIEnumerableResponseDto,
  ClaveSatResponsePagedResponseResponseDto,
  ClaveSatResponseResponseDto,
  CrearClaveSatRequest,
  GenericCatalogIEnumerableResponseDto,
  ObjectIEnumerableResponseDto,
  ObjectResponseDto,
} from '@schemas/catalog';

@Injectable({ providedIn: 'root' })
export class CatalogService extends ApiBaseService {
  private baseUrl = `${environment.BaseUrl}/Catalog/`;

  /** Lista paginada del catálogo. Devuelve únicamente los brokers vigentes; `nombre` es un filtro opcional por coincidencia parcial. */
  async brokers_paged(page: number, pageSize: number, nombre: string) {
    return this.get<BrokerResponsePagedResponseResponseDto>(`${this.baseUrl}brokers/paged`, {
      page: page.toString(),
      pageSize: pageSize.toString(),
      nombre: nombre,
    });
  }

  /** Un broker por su id. Devuelve también los dados de baja, con `activo: false`. */
  async getBrokers(idBroker: number) {
    return this.get<BrokerResponseResponseDto>(`${this.baseUrl}brokers/${idBroker}`);
  }

  /** Actualiza los datos de un broker vigente. Uno dado de baja responde 404. */
  async updateBrokers(idBroker: number, body: ActualizarBrokerRequest) {
    return this.put<BrokerResponseResponseDto>(`${this.baseUrl}brokers/${idBroker}`, body);
  }

  /** Da de baja un broker. Es baja lógica: la fila permanece porque api-clientes y api-facturacion la referencian desde sus tablas de relación. puede seguir mostrando un broker dado de baja. */
  async deleteBrokers(idBroker: number) {
    return this.delete<BooleanResponseDto>(`${this.baseUrl}brokers/${idBroker}`);
  }

  /** Comprueba qué build está corriendo: devuelve la palabra "cambios" y la fecha del servidor. responde incluso con la conexión caída: si contesta, el contenedor está sirviendo <b>esta</b> versión del código. Se agregó porque un tag mutable del registry retrocedió a una imagen vieja y el pipeline quedó en verde: sin una ruta nueva que pedir, no había forma de distinguir "no se desplegó" de "se desplegó y falla". La fecha va en hora local, no UTC: el contenedor fija `TZ=America/Mexico_City` y lo que se quiere verificar es justamente la hora que ve el servicio. Por eso también viaja la zona. */
  async deploymentCheck() {
    return this.get<ObjectResponseDto>(`${this.baseUrl}deployment-check`);
  }

  /** Obtiene el catálogo de giros de las empresas */
  async businessLines() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}business-lines`);
  }

  /** Obtiene el catálogo de estatus del timbrado de las facturas (Cancelada, En captura, Pagada, Timbrada, etc.) */
  async invoiceStampingStatus() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}invoice-stamping-status`);
  }

  /** Obtiene el catálogo de tipos de servicio */
  async serviceTypes() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}service-types`);
  }

  /** Obtiene el catálogo de tipos de salario */
  async salaryTypes() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}salary-types`);
  }

  /** Obtiene el catálogo de periodicidad */
  async periodicities() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}periodicities`);
  }

  /** Obtiene el catálogo de beneficios adicionales */
  async additionalBenefits() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}additional-benefits`);
  }

  /** Obtiene las empresas internas con sus claves SAT relacionadas */
  async companySatKeys() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}company-sat-keys`);
  }

  /** Obtiene los puestos del personal internos */
  async positions() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}positions`);
  }

  /** Obtiene el catálogo de bancos */
  async banks() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}banks`);
  }

  async civilsocieties() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}civilsocieties`);
  }

  /** Obtiene el catálogo de campos especiales con su tipo de dato asociado */
  async specialFields() {
    return this.get<CampoEspecialTipoDatoIEnumerableResponseDto>(`${this.baseUrl}special-fields`);
  }

  /** Obtiene los estatus de solicitudes de permiso */
  async leaveRequestStatuses() {
    return this.get<GenericCatalogIEnumerableResponseDto>(`${this.baseUrl}leave-request-statuses`);
  }

  /** Obtiene el catálogo de parentescos */
  async relationships() {
    return this.get<GenericCatalogIEnumerableResponseDto>(`${this.baseUrl}relationships`);
  }

  /** Obtiene el catálogo de géneros */
  async genders() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}genders`);
  }

  /** Obtiene el catálogo de estados de México */
  async statesOfMexico() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}states-of-mexico`);
  }

  /** Obtiene el catálogo de niveles de educación */
  async educationLevels() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}education-levels`);
  }

  /** Obtiene el catálogo de estados civiles */
  async maritalStatuses() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}marital-statuses`);
  }

  /** Obtiene el catálogo de tipos de contrato laboral */
  async employmentContractTypes() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}employment-contract-types`);
  }

  /** Obtiene el catálogo de esquemas de pago */
  async paySchemes() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}pay-schemes`);
  }

  /** Obtiene el catálogo de nacionalidades */
  async nationalities() {
    return this.get<ObjectIEnumerableResponseDto>(`${this.baseUrl}nationalities`);
  }

  /** Lista paginada del catálogo de claves del SAT. Devuelve únicamente las claves vigentes; `descripcion` e `idEstatus` son filtros opcionales. */
  async getSatClasses(page: number, pageSize: number, descripcion: string, idEstatus: number) {
    return this.get<ClaveSatResponsePagedResponseResponseDto>(`${this.baseUrl}sat-classes`, {
      page: page.toString(),
      pageSize: pageSize.toString(),
      descripcion: descripcion,
      idEstatus: idEstatus.toString(),
    });
  }

  /** Da de alta una clave del catálogo del SAT. <b>La clave la proporciona el cliente</b> (`idClaseSat`, por ejemplo 84111500): es el identificador que publica el SAT, no un consecutivo. Si ya está registrada responde 409. Exige un rol de administracion: el claim `idrole` del token debe estar en la lista `Authorization:SatClasses:Roles` de configuracion. Si no, responde 403 sin cuerpo. */
  async createSatClasses(body: CrearClaveSatRequest) {
    return this.post<ClaveSatResponseResponseDto>(`${this.baseUrl}sat-classes`, body);
  }

  /** Busca claves de productos/servicios del SAT por clave o descripción (hasta 100 resultados). catálogo completo está el listado paginado. Endpoint original en BillingCenter: `GET api/Billing/catalogs/sat-classes/search`. */
  async satClasses_search(description: string) {
    return this.get<ClaseSatVectorResponseIEnumerableResponseDto>(`${this.baseUrl}sat-classes/search`, {
      description: description,
    });
  }

  /** Obtiene una clave del SAT por su clave. A diferencia del listado, también devuelve las claves dadas de baja, con `activo = false`: así el cliente distingue "no existe" de "existe pero está de baja". */
  async getSatClassesById(id: number) {
    return this.get<ClaveSatResponseResponseDto>(`${this.baseUrl}sat-classes/${id}`);
  }

  /** Actualiza los datos descriptivos de una clave del SAT. La clave y el estatus no se tocan aquí: la primera identifica el registro y el segundo tiene su propio endpoint. Exige un rol de administracion: el claim `idrole` del token debe estar en la lista `Authorization:SatClasses:Roles` de configuracion. Si no, responde 403 sin cuerpo. */
  async updateSatClasses(id: number, body: ActualizarClaveSatRequest) {
    return this.put<ClaveSatResponseResponseDto>(`${this.baseUrl}sat-classes/${id}`, body);
  }

  /** Baja de una clave del SAT. Es <b>baja lógica</b>: el registro permanece porque hay CFDI timbrados que lo referencian; sólo deja de aparecer en el listado. Exige un rol de administracion: el claim `idrole` del token debe estar en la lista `Authorization:SatClasses:Roles` de configuracion. Si no, responde 403 sin cuerpo. */
  async deleteSatClasses(id: number) {
    return this.delete<BooleanResponseDto>(`${this.baseUrl}sat-classes/${id}`);
  }

  /** Cambia el estatus de una clave del SAT: 5177 Activo, 5178 En Desuso, 5179 Removida SAT. `Removida SAT` significa que el SAT la retiró de su catálogo; la clave sigue resolviéndose para los CFDI que ya la usaron. Exige un rol de administracion: el claim `idrole` del token debe estar en la lista `Authorization:SatClasses:Roles` de configuracion. Si no, responde 403 sin cuerpo. */
  async satClasses_estatus(id: number, body: CambiarEstatusClaveSatRequest) {
    return this.patch<BooleanResponseDto>(`${this.baseUrl}sat-classes/${id}/estatus`, body);
  }
}
